// src/systems/captureSystem.ts — Fase 3: fórmula de captura
import type { AsteriaSpecies, AsteriaInstance } from '../entities/asteria.js';
import type { PlayerState } from '../entities/player.js';

export type NucleoType = 'nucleo_vinculo' | 'nucleo_brillo' | 'nucleo_noche'; // solo vinculo en Fase 3

export const NUCLEO_DEFS: Record<NucleoType, { name:string; bonus:number; description:string }> = {
  nucleo_vinculo: { name:'Núcleo de Vínculo', bonus: 1.0, description:'Núcleo estándar. Buen vínculo inicial.' },
  nucleo_brillo:  { name:'Núcleo Brillo',   bonus: 1.5, description:'+50% vs Asterias brillantes (futuro).' },
  nucleo_noche:   { name:'Núcleo Noche',   bonus: 1.5, description:'+50% vs nocturnas.' },
};

export type CaptureContext = {
  species: AsteriaSpecies;
  wildInstance: { level:number; hp:number; maxHp:number }; // no es player instance aún
  nucleo: NucleoType;
  timePhase: 'amanecer'|'dia'|'atardecer'|'noche';
  playerBondAvg?: number; // futuro
};

// Fórmula provisional Fase 3 (inspirada en captura clásica pero original):
// base = species.captureRate (0.30..0.52)
// hpFactor = (1 - hpRatio) * 0.7 + 0.3  -> 0.3 a 1.0 (más baja salud = mayor)
// levelFactor =  (10 + (10 - level)*0.02)?? simplificado: nivel bajo +5% por nivel bajo
// bonusNucleo
// rareza ya en captureRate
// phase bonus: si nocturna de noche +0.15
export function calcCaptureChance(ctx: CaptureContext): number {
  const base = ctx.species.captureRate;
  const hpRatio = ctx.wildInstance.hp / Math.max(1, ctx.wildInstance.maxHp);
  const hpFactor = 0.30 + (1 - hpRatio) * 0.70; // 0.3 cuando llena, 1.0 cuando 0
  const level = ctx.wildInstance.level;
  const levelFactor = 1 + (10 - level) * 0.018; // nivel 3 → +0.12, nivel 8 → +0.036
  const nucleoBonus = NUCLEO_DEFS[ctx.nucleo]?.bonus ?? 1;
  let phaseBonus = 1;
  if(ctx.species.behavior==='nocturna' && ctx.timePhase==='noche') phaseBonus = 1.15;
  if(ctx.species.habitat.includes('agua') && ctx.timePhase==='noche') phaseBonus *= 1.05;
  let p = base * hpFactor * levelFactor * nucleoBonus * phaseBonus;
  // clamp 0.05 .. 0.92
  p = Math.max(0.05, Math.min(0.92, p));
  return p;
}

export function attemptCapture(ctx: CaptureContext): { success:boolean; chance:number; roll:number } {
  const chance = calcCaptureChance(ctx);
  const roll = Math.random();
  return { success: roll < chance, chance, roll };
}

// Inventario helpers
export function getNucleoCount(state: PlayerState, type: NucleoType='nucleo_vinculo'): number {
  return state.inventory.find(i=> i.id===type)?.qty ?? 0;
}
export function consumeNucleo(state: PlayerState, type: NucleoType='nucleo_vinculo'): boolean {
  const item = state.inventory.find(i=> i.id===type);
  if(!item || item.qty <=0) return false;
  item.qty -=1;
  return true;
}
